import { Icon, Stack } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import React, { ReactNode, useMemo, useState } from 'react';

import { TwoArrowsIcon } from '../../assets/assets';
import { DropdownMenuItem } from '../DropdownMenu/DropdownMenu';
import { DropdownMenuPopover, DropdownMenuPopoverProps } from '../DropdownMenu/DropdownMenuPopover';

export type SelectPopoverOption = { id: string; name: string };
type PopoverOption = DropdownMenuItem & { id: string };
type PopoverProps = Omit<DropdownMenuPopoverProps<PopoverOption>, 'open' | 'options'>;

export type SelectPopoverProps<T extends SelectPopoverOption> = {
  options: T[];
  selectedOption?: T;
  onSelect: (option: T) => void;
  children?: ReactNode;
  dataTestId?: string;
  popoverProps?: PopoverProps;
};

export const SelectPopover = <T extends SelectPopoverOption>({
  options,
  selectedOption,
  onSelect,
  children,
  dataTestId,
  popoverProps,
}: SelectPopoverProps<T>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const { sx, ...restPopoverProps } = popoverProps ?? {};

  const handleOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const popoverOptions: PopoverOption[] = useMemo(
    () =>
      options.map((option) => ({
        id: option.id,
        title: option.name,
        handler: () => onSelect(option),
      })),
    [options, onSelect],
  );

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{
          cursor: 'pointer',
          '& .MuiIcon-root': {
            color: isOpen ? 'primary.main' : 'text.secondary',
          },
          '&:hover .MuiIcon-root': {
            color: 'primary.main',
          },
        }}
        onClick={handleOpen}
        {...(dataTestId ? { 'data-test-id': dataTestId } : {})}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedOption?.name}</span>
        <Icon>
          <TwoArrowsIcon />
        </Icon>
      </Stack>

      <DropdownMenuPopover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        closeOnSelect
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        isSelected={(option) => option.id === selectedOption?.id}
        options={popoverOptions}
        autoFocus={false}
        sx={(theme) => ({
          ...theme.mixins.border(),
          '& .MuiMenu-paper': {
            ...theme.mixins.borderControl(),
          },
          ...((typeof sx === 'function' ? sx(theme) : sx) as SystemStyleObject),
        })}
        {...restPopoverProps}
      >
        {children}
      </DropdownMenuPopover>
    </>
  );
};
